import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { ArrowLeft } from 'lucide-react'
import { ButtonLink } from '@/components/generals/button-link'
import { Tilt } from '@/components/generals/tilt'
import { SectionHeading } from '@/components/generals/section-heading'
import { BRAND_ROOTS } from '@/constants/content'
import { MEDIA } from '@/constants/media'

export const StorySection: FC = (): ReactNode => {
    return (
        <section id='story' aria-labelledby='story-title' className='border-y border-sand bg-parchment/60 py-24 lg:py-32'>
            <div className='mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:items-center lg:gap-12'>
                <div className='order-last lg:order-first lg:col-span-5'>
                    <div className='relative mx-auto w-full max-w-sm'>
                        <div aria-hidden='true' className='absolute -inset-4 rounded-t-full rounded-b-[2.75rem] border border-gold/40' />

                        <Tilt max={5} className='rounded-t-full rounded-b-[2rem]'>
                            <div className='relative aspect-[2/3] overflow-hidden rounded-t-full rounded-b-[2rem] border border-gold/50 bg-forest-900 shadow-[0_40px_80px_-45px_rgba(15,42,30,0.8)]'>
                                <Image
                                    src={MEDIA.globeEmblem}
                                    alt='نشان طلایی KZ روی کره زمین، با کوهستان زاگرس، گیاهان دارویی و پرچم ایران'
                                    fill
                                    sizes='(min-width: 1024px) 24rem, 80vw'
                                    className='object-cover'
                                />
                            </div>
                        </Tilt>
                    </div>
                </div>

                <div className='lg:col-span-7'>
                    <SectionHeading
                        id='story-title'
                        eyebrow='داستان ما'
                        title='طبیعت زاگرس، میراث خوزستان، اصالت ایران'
                    />

                    <div className='mt-6 space-y-4 text-base leading-8 text-muted-foreground sm:text-lg sm:leading-9'>
                        <p>
                            خوزکیمیا زاگرس از پیوند دو سرزمین آمده است: کوه‌های سربه‌فلک‌کشیده زاگرس که گنجینه‌ای از گیاهان دارویی خودرو را در دل خود دارند، و خوزستان و فرهنگ بختیاری که دانش شناخت و استفاده از این گیاهان را نسل به نسل زنده نگه داشته‌اند.
                        </p>

                        <p>
                            ما این میراث را با دانش آزمایشگاهی امروز همراه کرده‌ایم تا گیاهان اصیل ایرانی، با کیفیتی که اندازه‌گیری و ثبت می‌شود، به خانه‌ها و بازارهای جهان برسند.
                        </p>
                    </div>

                    <dl className='mt-10 grid grid-cols-2 gap-x-6 gap-y-6 sm:grid-cols-4'>
                        {BRAND_ROOTS.map(root => (
                            <div key={root.title} className='border-s-2 border-gold/60 ps-4'>
                                <dt className='font-heading text-2xl font-bold text-forest-900'>
                                    {root.title}
                                </dt>

                                <dd className='mt-1 text-sm leading-6 text-muted-foreground'>
                                    {root.text}
                                </dd>
                            </div>
                        ))}
                    </dl>

                    <ButtonLink href='/about' variant='outline' className='mt-10'>
                        درباره خوزکیمیا زاگرس
                        <ArrowLeft aria-hidden='true' />
                    </ButtonLink>
                </div>
            </div>
        </section>
    )
}
